'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import Image from 'next/image'
import { login } from '@/lib/authService'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Loader2, ArrowRight, Sparkles } from 'lucide-react'
import Link from 'next/link'

export default function LoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    // Robustesse: lire depuis FormData pour supporter l'autofill navigateur
    const form = e.currentTarget
    const formData = new FormData(form)
    const submittedUsername = (formData.get('username') as string) || username
    const submittedPassword = (formData.get('password') as string) || password

    try {
      const response = await login({ username: submittedUsername, password: submittedPassword })

      // Si 2FA est requis (vérifier la valeur, pas seulement la présence)
      if ('requires_2fa' in response && response.requires_2fa === true) {
        // TODO: Gérer le 2FA
        setError('2FA non encore implémenté dans cette interface')
        setIsLoading(false)
        return
      }

      if ('requires_2fa_setup' in response && response.requires_2fa_setup === true) {
        // TODO: Gérer la configuration initiale du 2FA
        setError('Configuration 2FA requise - non encore implémentée')
        setIsLoading(false)
        return
      }

      // Si on a reçu les tokens, la connexion est réussie
      if ('access_token' in response) {
        // Forcer une navigation hard afin que le middleware
        // voie immédiatement les cookies côté serveur
        window.location.href = '/dashboard'
      } else {
        setError('Réponse inattendue du serveur')
        setIsLoading(false)
      }
    } catch (err: any) {
      console.error('Login error:', err)
      setError(err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Identifiants invalides')
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-primary/10 blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-primary/5 blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-96 w-96 rounded-full bg-primary/5 blur-3xl animate-pulse delay-500" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md px-4"
      >
        <Card className="backdrop-blur-sm bg-card/95 shadow-2xl border-primary/10">
          <CardHeader className="text-center pb-4 items-center justify-center">
            <Link href="/">
              <Image src="/logo_pluralisme_alpha.png" alt="Pluralisme" width={230} height={230} />
            </Link>
            <div className="py-2 border-b border-primary/10 w-full">
            </div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent pt-4 ">
              Bienvenue
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Connectez-vous pour accéder à votre espace d'analyse
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                </motion.div>
              )}

              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium">
                  Nom d'utilisateur
                </Label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  placeholder="Entrez votre nom d'utilisateur"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                  disabled={isLoading}
                  className="h-11 bg-background/50 border-muted-foreground/20 focus:border-primary transition-colors"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Mot de passe
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Entrez votre mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    disabled={isLoading}
                    className="h-11 pr-10 bg-background/50 border-muted-foreground/20 focus:border-primary transition-colors"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-11 px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full h-11 font-medium relative group"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connexion en cours...
                  </>
                ) : (
                  <>
                    Se connecter
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </Button>

              <div className="relative w-full">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-muted-foreground/20" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Ou</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full h-11 font-medium"
                disabled={isLoading}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Demander un accès
              </Button>
            </CardFooter>
          </form>
        </Card>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-xs text-muted-foreground mt-6"
        >
          En vous connectant, vous acceptez nos{' '}
          <a href="#" className="underline hover:text-primary transition-colors">
            Conditions d'utilisation
          </a>{' '}
          et notre{' '}
          <a href="#" className="underline hover:text-primary transition-colors">
            Politique de confidentialité
          </a>
        </motion.p>
      </motion.div>
    </div>
  )
}